import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, HeartHandshake, Newspaper, FolderOpen, Wallet, Mail, ArrowLeft, LogOut, ClipboardCheck, History, Vote, ShoppingBag, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { ASSO } from "@/constants";

const LINKS = [
  { label: "Vue d'ensemble", path: "/admin", icon: LayoutDashboard, end: true, id: "dashboard" },
  { label: "Adhérents", path: "/admin/membres", icon: Users, id: "membres", perm: "membres" },
  { label: "Bénévoles", path: "/admin/benevoles", icon: HeartHandshake, id: "benevoles", perm: "benevoles" },
  { label: "Contenus", path: "/admin/contenus", icon: Newspaper, id: "contenus", perm: "contenus" },
  { label: "Fil d'actualité", path: "/admin/fil-actualite", icon: Megaphone, id: "fil-actualite", perm: "contenus" },
  { label: "Documents & PV", path: "/admin/documents", icon: FolderOpen, id: "documents", perm: "documents" },
  { label: "Trésorerie", path: "/admin/tresorerie", icon: Wallet, id: "tresorerie", perm: "tresorerie" },
  { label: "Participations", path: "/admin/participations", icon: ClipboardCheck, id: "participations", perm: "participations" },
  { label: "Messages", path: "/admin/messages", icon: Mail, id: "messages", perm: "messages" },
  { label: "Journal d'activité", path: "/admin/journal", icon: History, id: "journal", perm: "journal" },
  { label: "Liste CET", path: "/admin/liste-cetf", icon: Vote, id: "liste-cetf", perm: "liste_cetf" },
  { label: "CETF Store", path: "/admin/store", icon: ShoppingBag, id: "store", perm: "store" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const visibleLinks = LINKS.filter((l) =>
    !l.perm || user?.role === "super_admin" || !user?.permissions || user.permissions.includes(l.perm)
  );

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
      isActive ? "bg-red-700 text-white" : "text-slate-300 hover:bg-red-900 hover:text-white"
    }`;

  return (
    <div className="min-h-screen bg-slate-100 lg:grid lg:grid-cols-[260px_1fr]">
      <aside className="hidden lg:flex flex-col bg-red-950 text-white min-h-screen sticky top-0" data-testid="admin-sidebar">
        <div className="p-5 flex items-center gap-2.5 border-b border-red-900 group">
          <img src={ASSO.logoTreeUrl} alt="" className="h-9 w-auto object-contain logo-tree" />
          <div>
            <p className="font-heading font-bold text-sm leading-tight">Back-office</p>
            <p className="text-xs text-slate-400">{ASSO.shortName}</p>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1" aria-label="Navigation admin">
          {visibleLinks.map(({ label, path, icon: Icon, end, id }) => (
            <NavLink key={path} to={path} end={end} className={linkClass} data-testid={`admin-nav-${id}`}>
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-red-900 space-y-1">
          <Button variant="ghost" className="w-full justify-start text-slate-300 hover:text-white hover:bg-red-900" asChild data-testid="admin-back-site-btn">
            <NavLink to="/"><ArrowLeft className="h-4 w-4 mr-2" />Retour au site</NavLink>
          </Button>
          <Button variant="ghost" className="w-full justify-start text-slate-300 hover:text-white hover:bg-red-900" onClick={handleLogout} data-testid="admin-logout-btn">
            <LogOut className="h-4 w-4 mr-2" />Déconnexion
          </Button>
        </div>
      </aside>

      <div className="flex flex-col min-w-0">
        <header className="lg:hidden bg-red-950 text-white">
          <div className="px-4 h-14 flex items-center justify-between">
            <span className="font-heading font-bold text-sm">Back-office {ASSO.shortName}</span>
            <div className="flex gap-1">
              <Button variant="ghost" size="icon" className="text-slate-300 hover:text-white" asChild data-testid="admin-mobile-back-site-btn">
                <NavLink to="/"><ArrowLeft className="h-5 w-5" /></NavLink>
              </Button>
              <Button variant="ghost" size="icon" className="text-slate-300 hover:text-white" onClick={handleLogout} data-testid="admin-mobile-logout-btn">
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
          <nav className="flex gap-1 overflow-x-auto px-3 pb-3" aria-label="Navigation admin mobile">
            {visibleLinks.map(({ label, path, icon: Icon, end, id }) => (
              <NavLink key={path} to={path} end={end} className={linkClass} data-testid={`admin-mobile-nav-${id}`}>
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
            ))}
          </nav>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet context={{ user }} />
        </main>
      </div>
    </div>
  );
}
